import { SeverityLevel } from "@microsoft/applicationinsights-web";

import {
  REQUEST_LOGIN,
  SET_ERROR,
  SET_AUTH_STATE,
  SET_TOKEN,
  SET_USER,
  REQUEST_USER_DETAILS,
  REQUEST_USER_DETAILS_SUCCESS,
  REQUEST_USER_DETAILS_FAIL,
  ACQUIRE_TOKEN_FAIL,
  SET_LOCALE
} from "./actionTypes";

// Utils
import getUserManager from "../../utils/oidc-utils";
import { getAppInsights } from "../../utils/telemetry/TelemetryService";
import errors from "../../utils/errors";

// Config
import { apiVersion, getMeUrl } from "../../config/apiConfig";

const trackAuthException = (label, error) => {
  const ai = getAppInsights();
  if (ai)
    ai.trackException({
      error: new Error(`${label}: ${JSON.stringify(error)}`),
      severityLevel: SeverityLevel.Error
    });
};

export const requestLogin = payload => ({
  type: REQUEST_LOGIN,
  payload
});

export const setUser = payload => ({
  type: SET_USER,
  payload
});

export const setError = payload => ({
  type: SET_ERROR,
  payload
});

export const setAccessToken = payload => ({
  type: SET_TOKEN,
  payload
});

export const setAuthState = payload => ({
  type: SET_AUTH_STATE,
  payload
});

export const acquireTokenFail = payload => ({
  type: ACQUIRE_TOKEN_FAIL,
  payload
});

export const initialize = cbk => async dispatch => {
  const userManager = getUserManager();

  const route =
    window.location.pathname + window.location.search + window.location.hash;

  const isRedirectCallback =
    window.location.search.includes("code=") ||
    window.location.search.includes("error=");

  try {
    if (isRedirectCallback) {
      const user = await userManager.signinRedirectCallback();
      dispatch(setUser(user));
      dispatch(setAccessToken(user.access_token));
      if (user.state) dispatch(setAuthState(user.state));
    } else {
      const user = await userManager.getUser();

      if (user && !user.expired) {
        dispatch(setUser(user));
        dispatch(setAccessToken(user.access_token));
      } else {
        await userManager.signinRedirect({ state: { route } });
      }
    }
  } catch (error) {
    trackAuthException("Sign-in Error", error);
    dispatch(acquireTokenFail(error));

    // oidc-client-ts throws a generic "No state in response" error (instead of
    // surfacing error/error_description) when Keycloak's callback has no
    // `state` param — which is the case for e.g. an expired login attempt.
    // Read them straight off the URL so callers can still tell what happened.
    const params = new URLSearchParams(window.location.search);
    dispatch(
      setError({
        error: params.get("error") || error.error || null,
        error_description:
          params.get("error_description") || error.error_description || null,
        message: error.message
      })
    );
  }

  cbk();
};

export const getTokenCallback = (callback, redirectState) => async dispatch => {
  const userManager = getUserManager();

  let user = await userManager.getUser();

  if (!user || user.expired) {
    user = await userManager.signinSilent().catch(error => {
      trackAuthException("Silent Token Renewal Error", error);
      dispatch(acquireTokenFail(error));
      return null;
    });
  }

  if (user && !user.expired) {
    dispatch(setAccessToken(user.access_token));
    callback(`Bearer ${user.access_token}`, user.access_token);
  } else {
    userManager.signinRedirect(redirectState ? { state: redirectState } : undefined);
  }
};

export const parseFetchOptions = (cbk, redirectState) => dispatch => {
  dispatch(
    getTokenCallback(
      Authorization =>
        cbk({
          headers: {
            Authorization,
            "api-version": apiVersion
          }
        }),
      redirectState
    )
  );
};

export const onSignIn = () => async dispatch => {
  dispatch(requestLogin());
  return getUserManager().signinRedirect();
};

export const onSignOut = () => dispatch => {
  dispatch(requestLogin());
  return getUserManager().signoutRedirect();
};

// Details
const requestUserDetails = () => ({
  type: REQUEST_USER_DETAILS
});

const requestUserDetailsSuccess = payload => ({
  type: REQUEST_USER_DETAILS_SUCCESS,
  payload
});

const requestUserDetailsFail = payload => ({
  type: REQUEST_USER_DETAILS_FAIL,
  payload
});

export const getUserDetails = () => dispatch => {
  dispatch(requestUserDetails());

  const onFail = () => {
    const ai = getAppInsights();
    if (ai)
      ai.trackException({
        error: new Error(`Failed to get user details`),
        severityLevel: SeverityLevel.Error
      });
  };

  dispatch(
    parseFetchOptions(async options => {
      const response = await fetch(getMeUrl, options).catch(error => {
        onFail();
        requestUserDetailsFail(error);
      });

      if (response && response.ok) {
        const data = await response.json();
        dispatch(requestUserDetailsSuccess(data));
      } else {
        onFail();
        dispatch(requestUserDetailsFail(errors.responseError(response)));
      }
    })
  );
};

// Locale
export const setLocale = payload => {
  localStorage.setItem("icLocale", payload);
  return {
    type: SET_LOCALE,
    payload
  };
};
